import React, { useEffect, useState } from 'react';
import { billingAPI } from '../api/client';
import { Card, Button, Badge } from '../components/ui';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

function CreditBar({ sub }) {
  const tokenLimit = sub?.plan?.max_tokens_monthly ?? -1;
  const tokensUsed = sub?.tokens_used_monthly ?? 0;
  const hasTokenLimit = tokenLimit !== -1;
  const percent = hasTokenLimit && tokenLimit > 0 ? Math.min(100, Math.round((tokensUsed / tokenLimit) * 100)) : (sub?.credit_percent || 0);
  const color = percent >= 95 ? '#ef4444' : percent >= 85 ? '#f97316' : percent >= 70 ? '#f59e0b' : '#10b981';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>
          {hasTokenLimit ? 'Tokens IA del período' : 'Crédito IA usado'}
        </span>
        <span style={{ fontSize: '13px', fontWeight: '800', color }}>{percent}%</span>
      </div>
      <div style={{ height: '10px', background: 'var(--bg-hover)', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${percent}%`, background: color, borderRadius: '99px', transition: 'width 0.5s' }} />
      </div>
      {percent >= 80 && percent < 100 && (
        <p style={{ fontSize: '12px', color, margin: 0 }}>
          ⚠️ Has consumido el {percent}% de tus tokens IA. Considera actualizar tu plan.
        </p>
      )}
      {percent >= 100 && (
        <p style={{ fontSize: '12px', color: '#ef4444', margin: 0, fontWeight: '600' }}>
          🚫 Tokens agotados. La IA está en modo manual. Actualiza tu plan para continuar.
        </p>
      )}
    </div>
  );
}

export default function MyBillingPage() {
  const [sub, setSub]         = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    Promise.all([billingAPI.getSubscription(), billingAPI.listPayments()])
      .then(([s, p]) => { setSub(s.data); setPayments(p.data); })
      .finally(() => setLoading(false));
  }, []);

  const handleCancel = async () => {
    if (!window.confirm('¿Cancelar suscripción? Tu plan seguirá activo hasta el final del período.')) return;
    setCancelling(true);
    try {
      await billingAPI.cancelSubscription();
      toast.success('Suscripción cancelada. Seguirás en tu plan hasta que venza.');
      const { data } = await billingAPI.getSubscription();
      setSub(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al cancelar');
    }
    setCancelling(false);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Cargando...</div>;

  const percent = sub?.credit_percent || 0;
  const remaining = sub?.ai_credit_remaining || 0;
  const planColor = { free: '#6b7280', starter: '#3b82f6', pro: '#7c3aed', agency: '#f59e0b' }[sub?.plan?.slug] || '#7c3aed';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '680px' }}>

      {/* Plan actual */}
      <Card style={{ borderLeft: `4px solid ${planColor}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: '800', fontSize: '20px' }}>
                Plan {sub?.plan?.name || 'Free'}
              </h2>
              <Badge color={sub?.status === 'active' ? '#10b981' : '#ef4444'}>
                {sub?.status === 'active' ? '● Activo' : sub?.status || 'Inactivo'}
              </Badge>
            </div>
            {sub?.current_period_end && (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
                {sub.status === 'cancelled' ? 'Activo hasta' : 'Renueva el'}{' '}
                {new Date(sub.current_period_end + 'Z').toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })}
                {' · '}{sub.days_remaining} días restantes
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to="/pricing" style={{ textDecoration: 'none' }}>
              <Button size="sm" icon="⬆️">Actualizar plan</Button>
            </Link>
            {sub?.status === 'active' && sub?.plan?.slug !== 'free' && (
              <Button size="sm" variant="ghost" loading={cancelling} onClick={handleCancel}>Cancelar</Button>
            )}
          </div>
        </div>

        {/* Barra de tokens */}
        <CreditBar sub={sub} />

        {/* Posts */}
        <div style={{ marginTop: '14px', padding: '12px 16px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Posts publicados este período</span>
            <span style={{ fontWeight: '800', fontSize: '16px' }}>
              {sub?.posts_used || 0}
              {sub?.plan?.max_posts > 0 && <span style={{ fontWeight: '400', color: 'var(--text-muted)', fontSize: '13px' }}> / {sub.plan.max_posts}</span>}
              {sub?.plan?.max_posts === -1 && <span style={{ fontWeight: '400', color: '#10b981', fontSize: '13px' }}> / ∞</span>}
            </span>
          </div>
          {sub?.plan?.max_posts > 0 && (() => {
            const postPct = Math.min(100, Math.round(((sub?.posts_used || 0) / sub.plan.max_posts) * 100));
            const postColor = postPct >= 95 ? '#ef4444' : postPct >= 80 ? '#f59e0b' : '#10b981';
            return (
              <div style={{ height: '6px', background: 'var(--bg-hover)', borderRadius: '99px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${postPct}%`, background: postColor, borderRadius: '99px', transition: 'width 0.5s' }} />
              </div>
            );
          })()}
        </div>
      </Card>

      {/* Features del plan */}
      {sub?.plan?.features?.length > 0 && (
        <Card>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '14px' }}>
            Incluido en tu plan
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
            {sub.plan.features.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <span style={{ color: planColor, flexShrink: 0 }}>✓</span> {f}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Historial de pagos */}
      <Card>
        <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '14px' }}>
          Historial de pagos
        </h3>
        {payments.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Sin pagos registrados.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {payments.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>{p.description || 'Suscripción TX-Flow AI'}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {p.gateway} · {new Date(p.created_at + 'Z').toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: '800', fontSize: '15px' }}>${p.amount} {p.currency}</div>
                  <Badge color={p.status === 'completed' ? '#10b981' : p.status === 'pending' ? '#f59e0b' : '#ef4444'}>
                    {p.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

    </div>
  );
}
